/**
 * Pure helpers for the `fitToWidth` scaling behaviour of `EmailJsxPreview`.
 * Kept separate from the React component so the math is unit-testable
 * without standing up jsdom layout primitives.
 */

export interface ComputeScaleArgs {
  /** Available width of the parent container (px). */
  parentWidth: number
  /** Intrinsic content width (px) — typically the parsed `<Container>` width. */
  intrinsicWidth: number
}

export interface ComputeScaleResult {
  scale: number
  /** True when the content already fits without downscaling. */
  fits: boolean
}

/**
 * Compute a CSS `transform: scale(...)` factor that downsizes the email so it
 * fits within `parentWidth`. Never upscales — emails rendered narrower than
 * the pane stay at intrinsic size and align top-left.
 *
 * Returns `scale === 1` when either dimension is non-positive so a bad
 * measurement (e.g. before the first ResizeObserver tick) doesn't collapse
 * the preview to nothing.
 */
export function computeScaleToFit(args: ComputeScaleArgs): ComputeScaleResult {
  const { parentWidth, intrinsicWidth } = args
  if (parentWidth <= 0 || intrinsicWidth <= 0) {
    return { scale: 1, fits: true }
  }
  if (parentWidth >= intrinsicWidth) {
    return { scale: 1, fits: true }
  }
  return { scale: parentWidth / intrinsicWidth, fits: false }
}

/**
 * Layout width for a fill-the-column preview: the column's width when it is
 * wider than the design (the email reflows to it, so its own background
 * fills the column), else the design width (which then scales down). Falls
 * back to the design width before the first measurement.
 */
export function computeFillLayoutWidth(args: ComputeScaleArgs): number {
  const { parentWidth, intrinsicWidth } = args
  return Math.max(Math.floor(parentWidth), intrinsicWidth)
}

/**
 * Given the unscaled rendered height of the inner content, compute the
 * height to reserve on the wrapper so the scaled output doesn't overflow
 * or leave dead space. `transform: scale(s)` reduces the visual height to
 * `s * intrinsicHeight` but the parent must still allocate that much space
 * because the transformed element is removed from layout flow.
 */
export function computeScaledHeight(
  intrinsicHeight: number,
  scale: number
): number {
  if (intrinsicHeight <= 0) {
    return 0
  }
  if (scale <= 0) {
    return 0
  }
  return Math.ceil(intrinsicHeight * scale)
}
